/**
 * Services/Tradesmen Page Tour - Guide users through tradesman directory
 * @author Ellis Reed
 */

"use client";

import React, { useState, useEffect, useCallback } from "react";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from "react-joyride";

const STORAGE_KEYS = { completed: "usland-services-tour-completed", skipped: "usland-services-tour-skipped" };

interface ServicesTourProps {
  onComplete?: () => void;
  shouldRun?: boolean;
}

export function ServicesTour({ onComplete, shouldRun = false }: ServicesTourProps) {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (shouldRun) {
      const timer = setTimeout(() => setRun(true), 500);
      return () => clearTimeout(timer);
    }
  }, [shouldRun]);

  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Tradesmen Directory</h2>
          <p className="text-gray-700">Find professional contractors and service providers for your properties.</p>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="category-filters"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
          <p className="text-gray-700">Browse by trade: Roofing, Plumbing, HVAC, Electrical, and more.</p>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="tradesman-cards"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Business Cards</h3>
          <p className="text-gray-700">Click any card to view contact details and services offered.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900"><strong>Tip:</strong> Cards flip to show full information.</p>
          </div>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="contact-tradesman"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Contact</h3>
          <p className="text-gray-700">Reach out directly via phone, email, or message.</p>
        </div>
      ),
      placement: "left",
      spotlightPadding: 8,
    },
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Find Your Professionals!</h2>
          <p className="text-gray-700">Connect with trusted tradespeople for your property needs.</p>
        </div>
      ),
      placement: "center",
    },
  ];

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      if (status === STATUS.FINISHED) localStorage.setItem(STORAGE_KEYS.completed, "true");
      else localStorage.setItem(STORAGE_KEYS.skipped, "true");
      onComplete?.();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  }, [onComplete]);

  const restartTour = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.completed);
      localStorage.removeItem(STORAGE_KEYS.skipped);
    }
    setStepIndex(0);
    setRun(true);
  }, []);

  useEffect(() => {
    window.addEventListener("restart-services-tour", restartTour);
    return () => window.removeEventListener("restart-services-tour", restartTour);
  }, [restartTour]);

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: { primaryColor: "#0066cc", textColor: "#1f2937", backgroundColor: "#ffffff", zIndex: 10000 },
        tooltip: { borderRadius: 8, padding: 20 },
        buttonNext: { backgroundColor: "#0066cc", fontSize: 14, fontWeight: 600, padding: "10px 20px" },
      }}
      locale={{ back: "Back", close: "Close", last: "Finish", next: "Next", skip: "Skip tour" }}
      disableScrolling
      disableOverlayClose
      spotlightClicks
    />
  );
}

export function restartServicesTour() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("restart-services-tour"));
}

export function isServicesTourCompleted(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEYS.completed) === "true" || localStorage.getItem(STORAGE_KEYS.skipped) === "true";
}
